import './CheckboxListDropdown.scss';
import * as React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { alphaNumSort } from '../utils/utils-sort';
import { Dropdown } from '@shared/ui-react/Dropdown';


export interface IFilterItem {
    title: string;
    value: number | string;
    active?: boolean;
}

@observer
export class CheckboxListDropdown extends React.Component<{
    title?: string,
    list: Map<string | number, IFilterItem>,
    // controlled component
    selected?: Array<string | number>,
    onChange?: (active: unknown[]) => void
}> {
    @observable filter = null;
    filterEl: HTMLInputElement;

    constructor(
        props: {
            title: string,
            list: Map<string, IFilterItem>
        }
    ) {
        super(props);
        makeObservable(this);
    }

    render() {
        let entries = Array.from(this.props.list.entries());
        let entriesFiltered = entries;
        if (this.filter) {
            entriesFiltered = entries.filter(e => e[1].title.toLowerCase().includes(this.filter.toLowerCase()));
        }
        const sortedEntries = entriesFiltered.sort((a, b) => {
            if (a[1].value == '-1') return -1; // dont === here, it can be -1 or '-1'
            if (b[1].value == '-1') return 1;
            return alphaNumSort(a[1].title.trim(), b[1].title.trim())
        });

        const { list, title } = this.props;
        const enabled = this.props.selected ? this.props.selected.length : entries.filter(e => e[1].active).length;
        const notComplete = enabled !== entries.length;
        let firstSelectedLabel = '-';
        if (enabled === 1) {
            firstSelectedLabel = this.props.selected?.length === 1 ? this.props.list.get(this.props.selected[0])?.title : Array.from(this.props.list.values()).filter(e => e.active)[0]?.title;
        }

        if (list['-1']) {
            entries.unshift(['-1', list['-1']]);
        }

        return (
            <Dropdown bodyClassName='checkbox-list-dropdown' buttonStyle={{ paddingRight: '0.25em' }} lazyRender={true}
                      onOpen={this.onDropdownOpen} onClose={this.onDropdownClose}>
                {title ? <>
                    {/*< className='checkbox-list-dropdown__button'>*/}
                        <span>{title}</span>
                        {notComplete && <span className='checkbox-list-dropdown__counts'>({enabled} / {entries.length})</span>}
                        <i className='iconf-caret' style={{ marginLeft: 'auto' }}/>
                    </>
                    :
                    <>
                        {enabled > 1 ? <span>{enabled} / {entries.length}</span> : <span className='ellipsis'>{firstSelectedLabel}</span>}
                        <i className='iconf-caret' style={{ marginLeft: 'auto' }}/>
                    </>
                }

                <>
                    <header className='flex-row gap-half' style={{ padding: '13px' }}>
                        <input type="checkbox" checked={!notComplete} onClick={this.toggleAll} readOnly={true}/>
                        <input type="text" value={this.filter ?? ''}
                               onChange={this.handleFilterChange}
                               onKeyDown={this.handleFilterKeyDown}
                               ref={el => this.filterEl = el}/>
                    </header>
                    <ul className='clickable'>
                        {sortedEntries.map(([key, val]) => (
                            <li key={val.value} onClick={() => this.toggleItem(val.value)}>
                                <input type='checkbox' checked={this.props.selected ? this.props.selected.includes(val.value) : val.active} readOnly={true}/> {val.title}
                            </li>
                        ))}
                    </ul>
                </>
            </Dropdown>
        );
    }

    @action
    toggleItem = key => {
        if (this.props.selected) {
            let selected = [...this.props.selected];
            if (selected.includes(key)) {
                selected = selected.filter(k => k != key);
            } else {
                selected.push(key);
            }
            this.props.onChange(selected);
        } else {
            const curr = this.props.list.get(key).active;
            this.props.list.get(key).active = !curr;
            this.callOnChange();
        }
    };

    @action
    all = () => {
        if (this.props.selected) {
            this.props.onChange(Array.from(this.props.list.keys()));
        } else {
            this.props.list.forEach((val, key) => this.props.list.get(key).active = true);
            this.callOnChange();
        }
    };

    @action
    none = () => {
        if (this.props.selected) {
            this.props.onChange([]);
        } else {
            this.props.list.forEach((val, key) => this.props.list.get(key).active = false);
            this.callOnChange();
        }
    };

    @action
    toggleAll = () => {
        const entries = Array.from(this.props.list.entries());
        const enabled = this.props.selected ? this.props.selected.length : entries.filter(e => e[1].active).length;
        const notComplete = enabled !== entries.length;

        if (notComplete) {
            this.all();
        } else {
            this.none();
        }
    };

    callOnChange = () => {
        if (typeof this.props.onChange === 'function') {
            const active = Array.from(this.props.list.values()).filter(e => e.active).map(e => e.value);
            this.props.onChange(active);
        }
    }

    handleFilterChange = e => {
        this.filter = e.currentTarget.value;
    }

    onDropdownOpen = () => {
        setTimeout(() => {
            this.filterEl?.focus();
        }, 100);
    }

    onDropdownClose = () => {
        this.filter = null;
    }

    handleFilterKeyDown = e => {
        if (e.key === 'Escape') {
            this.filter = null;
        }
        if (e.key === 'Enter') {
            const filteredItems = Array.from(this.props.list.values()).filter(e => e.title.toLowerCase().includes(this.filter.toLowerCase()));
            if (filteredItems.length === 1) {
                this.toggleItem(filteredItems[0].value);
            }
        }
    }
}
